<script setup lang="ts">
interface TagOption {
  id: string | number;
  name: string;
  type?: string;
  category?: string;
}

interface Props {
  tags?: TagOption[];
  cuisines?: TagOption[];
  allergens?: TagOption[];
  initialTags?: (string | number)[];
  initialCuisines?: (string | number)[];
  initialDate?: string;
  showDate?: boolean;
  scrollOnChange?: boolean;
  syncUrl?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  tags: () => [],
  cuisines: () => [],
  allergens: () => [],
  initialTags: () => [],
  initialCuisines: () => [],
  initialDate: "",
  showDate: true,
  scrollOnChange: true,
  syncUrl: true,
});

const emit = defineEmits<{
  filter: [
    payload:
      | {
          tags?: { id: string | number }[];
          cuisines?: { id: string | number }[];
        }
      | false,
    meta?: { occasionCount: number; attributeCount: number },
  ];
  date: [date: string];
  restore: [payload: { date: string; tags: (string | number)[]; cuisines: (string | number)[] }];
}>();

// Hard-coded list of valid occasions matching old site
const OCCASIONS = [
  "Breakfast",
  "Lunch",
  "Dinner",
  "Snacks",
  "Desserts",
  "Drinks",
  "Event",
  "Finger Food",
  "Buffet",
  "Canapés",
  "Platters",
  "Sweets",
  "Cakes",
  "Pastries",
  "Cocktails / Bar",
  "Tea / Coffee",
];

const selectedTags = ref<(string | number)[]>(props.initialTags);
const selectedCuisines = ref<(string | number)[]>(props.initialCuisines);
const selectedDate = ref(props.initialDate);
const selectedCalendarDay = ref<Record<string, unknown> | false>(
  props.initialDate ? buildCalendarDay(props.initialDate) : false,
);

// Which dropdown is open: 'date' | 'occasion' | 'cuisine' | 'attribute' | null
const openDropdown = ref<"date" | "occasion" | "cuisine" | "attribute" | null>(null);
const filterBarRef = ref<HTMLElement | null>(null);

function buildCalendarDay(dateStr: string): Record<string, unknown> {
  const d = new Date(dateStr + "T12:00:00");
  if (isNaN(d.getTime())) return false as unknown as Record<string, unknown>;
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return {
    full: dateStr,
    number: String(d.getDate()),
    month: months[d.getMonth()],
    year: String(d.getFullYear()),
  };
}

// Items that appear in the cuisines API but are not actual cuisines
const NON_CUISINES = new Set([
  "art",
  "canapes",
  "canapés",
  "desserts",
  "drinks",
  "snacks",
  "sweets",
  "cakes",
  "pastries",
  "platters",
  "cocktails",
  "coffee",
  "tea",
  "breakfast",
  "buffet",
  "finger food",
  "event",
  "pop up",
  "pop-up",
]);

const tagOptions = computed(() =>
  props.tags
    .filter((t) => t.type === "product" && OCCASIONS.includes(t.name))
    .map((t) => ({ name: t.name, value: t.id })),
);

const cuisineOptions = computed(() =>
  props.cuisines
    .filter((c) => !NON_CUISINES.has(c.name.toLowerCase().trim()))
    .map((c) => ({ name: c.name, value: c.id })),
);

const attributeOptions = computed(() =>
  props.tags
    .filter((t) => t.type === "maker" && t.category === "attribute")
    .map((t) => ({ name: t.name, value: t.id })),
);

const occasionIdSet = computed(() => new Set(tagOptions.value.map((o) => o.value)));
const attributeIdSet = computed(() => new Set(attributeOptions.value.map((o) => o.value)));

const selectedOccasionIds = computed<(string | number)[]>({
  get: () => selectedTags.value.filter((id) => occasionIdSet.value.has(id)),
  set: (ids) => {
    selectedTags.value = [
      ...ids,
      ...selectedTags.value.filter((id) => !occasionIdSet.value.has(id)),
    ];
  },
});

const selectedAttributeIds = computed<(string | number)[]>({
  get: () => selectedTags.value.filter((id) => attributeIdSet.value.has(id)),
  set: (ids) => {
    selectedTags.value = [
      ...ids,
      ...selectedTags.value.filter((id) => !attributeIdSet.value.has(id)),
    ];
  },
});

const hasDate = computed(() => !!selectedDate.value);
const hasOccasion = computed(() => selectedOccasionIds.value.length > 0);
const hasAttribute = computed(() => selectedAttributeIds.value.length > 0);
const hasCuisine = computed(() => selectedCuisines.value.length > 0);
const hasAny = computed(
  () => hasDate.value || selectedTags.value.length > 0 || selectedCuisines.value.length > 0,
);

const dateChipLabel = computed(() => {
  if (!selectedCalendarDay.value) return "Date";
  const day = selectedCalendarDay.value as Record<string, unknown>;
  const monthName = (day.month as string) || "";
  return `${day.number} ${monthName.slice(0, 3)}`;
});

const cuisineChipLabel = computed(() =>
  selectedCuisines.value.length ? `Cuisine · ${selectedCuisines.value.length}` : "Cuisine",
);

const occasionChipLabel = computed(() =>
  selectedOccasionIds.value.length ? `Occasion · ${selectedOccasionIds.value.length}` : "Occasion",
);

const attributeChipLabel = computed(() =>
  selectedAttributeIds.value.length
    ? `Attributes · ${selectedAttributeIds.value.length}`
    : "Attributes",
);

function toggleDropdown(name: "date" | "occasion" | "cuisine" | "attribute") {
  openDropdown.value = openDropdown.value === name ? null : name;
}

function closeDropdown() {
  openDropdown.value = null;
}

function applyFilters() {
  const hasFilters = selectedTags.value.length > 0 || selectedCuisines.value.length > 0;
  if (!hasFilters) {
    emit("filter", false);
    return;
  }
  const occasionCount = selectedOccasionIds.value.length;
  const attributeCount = selectedAttributeIds.value.length;
  const splitResolved = occasionCount + attributeCount === selectedTags.value.length;

  emit(
    "filter",
    {
      ...(selectedTags.value.length > 0 ? { tags: selectedTags.value.map((id) => ({ id })) } : {}),
      ...(selectedCuisines.value.length > 0
        ? { cuisines: selectedCuisines.value.map((id) => ({ id })) }
        : {}),
    },
    splitResolved ? { occasionCount, attributeCount } : undefined,
  );
}

async function clearAll() {
  // Clear the URL FIRST and await it before touching any state. The
  // previous "Clear filters" link in the page wrapper had this safeguard
  // to keep the bar from re-restoring stale query params on remount;
  // bringing it down into the bar's own clearAll means the "Clear all"
  // button gets the same guarantee whether the parent page wraps it or
  // not. Harmless overhead when the bar isn't being remounted.
  const router = useRouter();
  const route = useRoute();
  if (props.syncUrl && Object.keys(route.query).length > 0) {
    await router.replace({ path: route.path, query: {} });
  }
  selectedTags.value = [];
  selectedCuisines.value = [];
  selectedDate.value = "";
  selectedCalendarDay.value = false;
  openDropdown.value = null;
  emit("filter", false);
  emit("date", "");
}

function clearDate() {
  selectedDate.value = "";
  selectedCalendarDay.value = false;
  emit("date", "");
}

function clearOccasion() {
  selectedTags.value = selectedTags.value.filter((id) => attributeIdSet.value.has(id));
}

function clearAttribute() {
  selectedTags.value = selectedTags.value.filter((id) => occasionIdSet.value.has(id));
}

function clearCuisine() {
  selectedCuisines.value = [];
}

function handleDateSelect(date: { full: string }) {
  if (selectedDate.value === date.full) {
    selectedDate.value = "";
    selectedCalendarDay.value = false;
    emit("date", "");
  } else {
    selectedDate.value = date.full;
    selectedCalendarDay.value = date as Record<string, unknown>;
    emit("date", date.full);
  }
  openDropdown.value = null;
  if (props.scrollOnChange) {
    nextTick(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  }
}

function handleOutsideClick(e: MouseEvent) {
  if (!filterBarRef.value) return;
  if (!filterBarRef.value.contains(e.target as Node)) {
    openDropdown.value = null;
  }
}

onMounted(() => {
  document.addEventListener("click", handleOutsideClick);
  if (props.initialDate || props.initialTags.length || props.initialCuisines.length) {
    emit("restore", {
      date: props.initialDate,
      tags: props.initialTags,
      cuisines: props.initialCuisines,
    });
  }
});

onUnmounted(() => {
  document.removeEventListener("click", handleOutsideClick);
});

watch(
  [selectedTags, selectedCuisines],
  () => {
    applyFilters();
    if (props.scrollOnChange) {
      nextTick(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
    }
  },
  { deep: true },
);

// Expose `clearAll` so the parent page can wire it to its own "Clear filters"
// affordance — otherwise it'd have to bump a `:key` to remount the bar.
defineExpose({ clearAll });
</script>

<template>
  <div
    ref="filterBarRef"
    class="filter-bar"
  >
    <!-- Mobile: Clear all sits on its own row ABOVE the pills row so
         it can't slip behind the horizontally-scrolling carousel.
         Desktop still shows it inline at the end of the pills row. -->
    <div
      v-if="hasAny"
      class="filter-bar__clear-row"
    >
      <button
        class="filter-bar__clear-all"
        type="button"
        @click="clearAll"
      >
        Clear all
      </button>
    </div>
    <div class="filter-bar__pills">
      <button
        v-if="showDate"
        class="filter-bar__pill"
        :class="{
          'filter-bar__pill--active': hasDate,
          'filter-bar__pill--open': openDropdown === 'date',
        }"
        type="button"
        @click="toggleDropdown('date')"
      >
        <fa
          v-if="!hasDate"
          class="filter-bar__pill-icon"
          :icon="['fal', 'calendar']"
        />
        <span>{{ dateChipLabel }}</span>
        <fa
          v-if="hasDate"
          class="filter-bar__pill-clear"
          :icon="['fal', 'xmark']"
          @click.stop="clearDate"
        />
        <fa
          v-else
          class="filter-bar__pill-caret"
          :icon="['fal', 'chevron-down']"
        />
      </button>

      <button
        v-if="tagOptions.length"
        class="filter-bar__pill"
        :class="{
          'filter-bar__pill--active': hasOccasion,
          'filter-bar__pill--open': openDropdown === 'occasion',
        }"
        type="button"
        @click="toggleDropdown('occasion')"
      >
        <span>{{ occasionChipLabel }}</span>
        <fa
          v-if="hasOccasion"
          class="filter-bar__pill-clear"
          :icon="['fal', 'xmark']"
          @click.stop="clearOccasion"
        />
        <fa
          v-else
          class="filter-bar__pill-caret"
          :icon="['fal', 'chevron-down']"
        />
      </button>

      <button
        v-if="cuisineOptions.length"
        class="filter-bar__pill"
        :class="{
          'filter-bar__pill--active': hasCuisine,
          'filter-bar__pill--open': openDropdown === 'cuisine',
        }"
        type="button"
        @click="toggleDropdown('cuisine')"
      >
        <span>{{ cuisineChipLabel }}</span>
        <fa
          v-if="hasCuisine"
          class="filter-bar__pill-clear"
          :icon="['fal', 'xmark']"
          @click.stop="clearCuisine"
        />
        <fa
          v-else
          class="filter-bar__pill-caret"
          :icon="['fal', 'chevron-down']"
        />
      </button>

      <button
        v-if="attributeOptions.length"
        class="filter-bar__pill"
        :class="{
          'filter-bar__pill--active': hasAttribute,
          'filter-bar__pill--open': openDropdown === 'attribute',
        }"
        type="button"
        @click="toggleDropdown('attribute')"
      >
        <span>{{ attributeChipLabel }}</span>
        <fa
          v-if="hasAttribute"
          class="filter-bar__pill-clear"
          :icon="['fal', 'xmark']"
          @click.stop="clearAttribute"
        />
        <fa
          v-else
          class="filter-bar__pill-caret"
          :icon="['fal', 'chevron-down']"
        />
      </button>

      <button
        v-if="hasAny"
        class="filter-bar__clear-all filter-bar__clear-all--inline"
        type="button"
        @click="clearAll"
      >
        Clear all
      </button>
    </div>

    <div
      v-if="openDropdown"
      class="filter-bar__dropdown"
      :class="`filter-bar__dropdown--${openDropdown}`"
    >
      <Calendar
        v-if="openDropdown === 'date'"
        :chosen-date="selectedCalendarDay"
        @return-date="handleDateSelect"
      />
      <CheckboxList
        v-else-if="openDropdown === 'occasion'"
        v-model="selectedOccasionIds"
        title=""
        :options="tagOptions"
        column
      />
      <CheckboxList
        v-else-if="openDropdown === 'cuisine'"
        v-model="selectedCuisines"
        title=""
        :options="cuisineOptions"
        column
      />
      <CheckboxList
        v-else-if="openDropdown === 'attribute'"
        v-model="selectedAttributeIds"
        title=""
        :options="attributeOptions"
        column
      />
    </div>
  </div>
</template>

<style lang="scss">
$laptop: 1200px;
$tablet: 768px;
$mobile: 600px;

.filter-bar {
  position: relative;
  // Shrink the bar to the natural width of its pills so the absolutely-
  // positioned dropdown (width: 100% of this container) matches the
  // pills row exactly — previous `width: 100%` made the dropdown
  // overshoot the right edge of the last pill into empty body space.
  // `max-width: 100%` keeps it bounded if pills overflow.
  width: fit-content;
  max-width: 100%;
  margin: 1rem 0 1.5rem;

  &__pills {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.75rem;
    // Mobile: keep the chips on a single track. A small phone (~375px)
    // can't fit Date + Occasion + Cuisine + "Clear all" on one row.
    // Horizontal scroll preserves the one-row intent without truncating.
    @media (max-width: $tablet) {
      flex-wrap: nowrap;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
      &::-webkit-scrollbar {
        display: none;
      }
      padding: 0 0.125rem;
      gap: 0.5rem;
    }
  }

  &__pill {
    appearance: none;
    -webkit-appearance: none;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    min-height: var(--tap);
    padding: 0 1rem;
    // Borderless idle pill — was a grey-3 outline that read as a hard
    // black ring against the warm canvas. Now matches the nav links +
    // allergen Filters button family: transparent border, soft cream
    // fill, primary text. Hover/active wash with pink-4 + pink-1 ink
    // mirrors those same surfaces.
    border: 1px solid transparent;
    border-radius: var(--radius);
    background: var(--color-grey-4);
    color: var(--color-grey-1);
    font-family: inherit;
    font-size: 0.95rem;
    font-weight: var(--weight-light);
    cursor: pointer;
    @media (max-width: $tablet) {
      // Lock width so flex-wrap:nowrap doesn't shrink the pills smaller
      // than their label text on a narrow screen.
      flex-shrink: 0;
      padding: 0 0.75rem;
      gap: 0.375rem;
    }
    transition:
      background-color var(--dur-fast) var(--ease-out),
      border-color var(--dur-fast) var(--ease-out),
      color var(--dur-fast) var(--ease-out);

    &:hover {
      background: var(--color-pink-4);
      color: var(--color-pink-1);
      border-color: var(--color-pink-4);
      .filter-bar__pill-caret,
      .filter-bar__pill-icon {
        color: var(--color-pink-1);
      }
    }

    // Label text matched to the search-bar input — 0.9rem at light
    // weight so the pills don't read heavier than the input next to
    // them in the navbar / filter strip.
    span {
      font-size: 0.9rem;
      font-weight: var(--weight-light);
    }

    &-icon,
    &-caret,
    &-clear {
      font-size: 0.85rem;
    }

    &-clear {
      cursor: pointer;
      padding: 0.25rem;
      margin-right: -0.25rem;
      &:hover {
        opacity: 0.7;
      }
    }

    // Open (dropdown visible): same pink wash as hover so the pill
    // doesn't snap to a sharp pink-1 border that conflicts with the
    // softer borderless family.
    &--open {
      background: var(--color-pink-4);
      color: var(--color-pink-1);
      border-color: var(--color-pink-4);
      .filter-bar__pill-caret,
      .filter-bar__pill-icon {
        color: var(--color-pink-1);
      }
    }

    // Selected chip: soft pink-4 wash with pink-1 ink. Matches the
    // ListFilter selected style on the maker page so all "active
    // filter" chrome reads as the same family across the site.
    &--active {
      background: var(--color-pink-4);
      border-color: var(--color-pink-4);
      color: var(--color-pink-1);
      .filter-bar__pill-caret,
      .filter-bar__pill-icon,
      .filter-bar__pill-clear {
        color: var(--color-pink-1);
      }
    }
  }

  // Mobile-only standalone Clear-all row, sits above the chips so it
  // can't slip behind the horizontally-scrolling pills strip.
  &__clear-row {
    display: none;
    @media (max-width: $tablet) {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 0.5rem;
    }
  }

  &__clear-all {
    appearance: none;
    background: transparent;
    border: 0;
    padding: 0 0.25rem;
    color: var(--color-grey-2);
    font-size: 0.9rem;
    font-family: inherit;
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 0.25rem;
    &:hover {
      color: var(--color-grey-1);
    }
    // Inline variant only renders on desktop — on mobile we use the
    // standalone row above the pills.
    &--inline {
      @media (max-width: $tablet) {
        display: none;
      }
    }
  }

  &__dropdown {
    position: absolute;
    top: calc(var(--tap) + 1rem);
    left: 0;
    right: 0;
    // FooterBar sits at z-index 3333 (site/app/components/FooterBar.vue) and
    // was stacking over this dropdown on short pages where the filter bar
    // sits near the bottom of the viewport. Needs to clear that.
    z-index: 3334;
    // Span the full width of the filter bar so every dropdown (date /
    // occasion / cuisine) lines up edge-to-edge with the bar above —
    // no visual jump when switching between filters, and the extra
    // breathing room means long checkbox labels ("Cocktails / Bar",
    // "Tea / Coffee", etc.) never need to be clipped or shrunk.
    width: 100%;
    padding: 1rem;
    background: var(--color-white-1);
    border: 1px solid var(--color-grey-3);
    border-radius: var(--radius);
    box-shadow: var(--shadow-2);
    // Cap the dropdown so it can't extend past the viewport when a long
    // cuisine list opens — scroll internally instead. 24rem is roughly
    // 6 checkbox rows plus the date picker's calendar height, generous
    // enough that most users never see a scrollbar.
    max-height: 24rem;
    overflow-y: auto;

    @media (max-width: $tablet) {
      left: 0;
      right: 0;
      width: auto;
    }

    // Calendar inside the dropdown should be borderless and snug
    .calendar {
      border: none;
      padding: 0;
      border-radius: 0;
      max-width: none;
    }

    // Checkbox list compact spacing inside the dropdown.
    // Force single-line labels so "Cocktails / Bar" etc. don't break
    // onto a second row inside the 18rem dropdown.
    .checkbox-list {
      &__item {
        margin: 0;
        white-space: nowrap;
        span {
          font-size: 0.85rem;
          white-space: nowrap;
        }
      }
    }
  }
}
</style>
